import axios from 'api/axios-configure'
import { Grid } from "@mui/material"
import { AlertBanner, AlertBannerText, Button, Tooltip } from 'monday-ui-react-core'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ArrowLeft, PersonFillGear, Trash3, Envelope } from 'react-bootstrap-icons'
import { Link } from 'react-router-dom'
import './UsersForm.scss'
import AddUsersModal from './AddUsersModal'
import { UITableReport } from 'components/UITableReport'
import UpdateUsersRole from './UpdateUsersRole'
import { AuthSessionContext } from 'api/AuthSession'
import RemoveUserModal from './RemoveUserModal'
import { UserRole } from 'utils/Constants'
import { toast } from 'react-toastify'
import { StatementContext } from 'utils/context/StatementsContext'

const UsersForm = () => {

    const { getUserPermissionsSummary } = useContext(StatementContext)
    const { userName, IS_ADMIN, IS_OWNER, setUserRole, getSession } = useContext(AuthSessionContext)

    const [tableData, setTableData] = useState([])

    const [displayBanner, setDisplayBanner] = useState(false)
    const [bannerMessage, setBannerMessage] = useState('')
    const [bannerStyle, setBannerStyle] = useState('positive')

    const [openModal, setOpenModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isFetching, setIsFetching] = useState(false)

    const [organizationRole, setOrganizationRole] = useState()
    const [emailList, setEmailList] = useState()

    const [openUserRoleModal, setOpenUserRoleModal] = useState(false)
    const [userIdToUpdate, setUserIdToUpdate] = useState('')
    const [userEmailToUpdate, setUserEmailToUpdate] = useState('')
    const [currentUserRole, setCurrentUserRole] = useState('')
    const [newUserRoleId, setNewUserRoleId] = useState('')

    const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false)
    const [userEmailToRemove, setUserEmailToRemove] = useState('')

    const clearBanner = () => {
        setDisplayBanner(false)
        setBannerMessage('')
        setBannerStyle('positive')
    }

    const displayErrorMessage = (message) => {
        setBannerMessage(message)
        setBannerStyle('negative')
        setDisplayBanner(true)
    }

    const displaySuccessMessage = (message) => {
        setBannerMessage(message)
        setBannerStyle('positive')
        setDisplayBanner(true)

        setTimeout(() => {
            clearBanner()
        }, 5000);
    }

    const displayActionByRoles = (row) => {
        if (row.original.orgRoleId === UserRole.API) 
            return false;

        if (IS_ADMIN && row.original.orgRoleId === UserRole.OWNER) 
            return false;

        return true
    }

    const columns = useMemo(
        () => [
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'email',
                header: 'User ID (Email)'
            },
            {
                size: 200,
                maxSize: 200,
                accessorKey: 'firstName',
                header: 'First Name'
            },
            {
                size: 200,
                maxSize: 200,
                accessorKey: 'lastName',
                header: 'Last Name'
            },
            {
                size: 100,
                maxSize: 100,
                accessorKey: 'orgRole',
                header: 'Org Role'
            },
            {
                header: 'Actions',
                size: 60,
                enableSorting: false,
                maxSize: 60,
                accessorKey: 'actions',
                Cell: ({row}) => {
                    return (
                        <div className='d-flex align-items-center'>
                            {(displayActionByRoles(row)) && (
                                <Tooltip content='Update Role'>
                                    <PersonFillGear 
                                        className='payout-icon'
                                        size={15}
                                        onClick={() => handleUpdateUserRole(row.original)}
                                    />
                                </Tooltip>
                            )}
                            {(row.original.email !== userName && displayActionByRoles(row)) && (
                                <Tooltip content='Remove User'>
                                    <Trash3 
                                        className='payout-icon'
                                        size={15}
                                        onClick={() => handleRemoveUser(row.original)}
                                    />
                                </Tooltip>
                            )}
                            {((IS_ADMIN || IS_OWNER) && row.original.needsResend === true) && (
                                <Tooltip content='Resend Invitation Email'>
                                    <Envelope 
                                        className='payout-icon'
                                        size={15}
                                        onClick={() => handleResendEmail(row.original.email)}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    )
                }
            }
        ]
    )

    useEffect(() => {
        getSession()
        getUserPermissionsSummary()
        getUsersList()
    }, [])

    const getUsersList = () => {
        clearModal()
        clearUpdateUserModal()
        clearBanner()

        if (!tableData.length)
            setIsLoading(true)
        else
            setIsFetching(true)

        axios
            .get('/GetOrganizationUsers')
            .then(response => {
                setTableData(response.data.map((user, index) => {
                    return {
                        orgUserId: user.orgUserId,
                        email: user.email,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        orgRole: user.orgRole,
                        orgRoleId: user.orgRoleId,
                        needsResend: user.needsResend
                    }
                }))
            })
            .catch(err => {
                console.error(err)
            })
            .finally(() => {
                setIsLoading(false)
                setIsFetching(false)
            })
    }

    const handleResendEmail = (email) =>{
        axios
            .post(`/ResendEmailInvitation`, email)
            .then(response => {
                toast.success('Email invitation has been sent', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                    className: 'toastify__toast--success-custom',
                    bodyClassName: 'toastify-body',
                    progressClassName: 'toastify-progress-bar',
                })
            })
            .catch(err => {
                console.error(err)
            })
    }

    const clearUpdateUserModal = () => {
        setUserIdToUpdate('')
        setUserEmailToUpdate('')
        setCurrentUserRole('')
        setNewUserRoleId('')
    }

    const handleUpdateUserRole = (row) => {
        setCurrentUserRole(row.orgRole)
        setUserIdToUpdate(row.orgUserId)
        setUserEmailToUpdate(row.email)
        setOpenUserRoleModal(true)
    }

    const handleUpdateUserModalSave = () => {
        const payload = {
            orgUserId: userIdToUpdate,
            newRole: newUserRoleId
        }

        setIsFetching(true)
        handleUpdateUserModalClose()

        axios
            .put('/UpdateUserOrganizationRole', payload)
            .then(response => {
                getUsersList()
                displaySuccessMessage(response.data.message)
                
                if (userName === userEmailToUpdate) {
                    setUserRole(payload.newRole)
                }
            })
            .catch(err => {
                console.error(err)
                displayErrorMessage(err.response.data.message)
            })
            .finally(() => {
                setIsFetching(false)
            })
    }

    const handleUpdateUserModalClose = () => {
        setOpenUserRoleModal(false)

        clearUpdateUserModal()
    }

    const clearModal = () => {
        setEmailList()
        setOrganizationRole()
    }

    const handleAddUser = () => {
        clearBanner()
        clearModal()
        setOpenModal(true)
    }

    const handleModalClose = () => {
        setOpenModal(false)

        clearModal()
    }

    const handleModalSave = () => {
        setIsFetching(true)
        clearBanner()
        setOpenModal(false)

        const body = {
            emailList: emailList,
            role: organizationRole.value
        }

        axios
            .post('/AddOrganizationUsers', body)
            .then(response => {
                getUsersList()
                displaySuccessMessage(response.data.message)
            })
            .catch(err => {
                displayErrorMessage(err.response.data.message)
            })
            .finally(() => {
                setIsFetching(false)
                clearModal()
            })
    }

    const clearRemoveUserModal = () => {
        setOpenRemoveUserModal(false)
        setUserEmailToRemove('')
    }

    const handleRemoveUser = (row) => {
        setUserEmailToRemove(row.email)
        setOpenRemoveUserModal(true)
    }

    const handleRemoveUserConfirm = () => {
        setIsFetching(true)
        axios
            .post('/RemoveUserFromOrganization', { email: userEmailToRemove})
            .then(response => {
                getUsersList()
                displaySuccessMessage(response.data.message)
            })
            .catch(err => {
                displayErrorMessage(err.response.data.message)
            })
            .finally(() => {
                setIsFetching(false)
                clearRemoveUserModal()
            })
    }
    
    const handleRemoveUserClose = () => {
        clearRemoveUserModal()
    }

    return (
        <>
            <div className="mx-2 form">
                <Grid container spacing={2} className="">
                    <Grid item xs={12} style={{paddingTop: '0px'}}>
                        <Link
                            className="d-flex align-items-center"
                            to="/dashboard"
                        >
                            <ArrowLeft size={13} />
                            <span className="ml-2"> Back</span>
                        </Link>
                    </Grid>
                    <Grid item xs={12}>
                        <h2>Users</h2>
                    </Grid>
                    <Grid item xs={12}>
                        <h4>List of users</h4>
                    </Grid>
                    {displayBanner && (
                        <Grid item xs={12}>
                            <AlertBanner backgroundColor={bannerStyle} isCloseHidden>
                                <AlertBannerText text={bannerMessage} />
                            </AlertBanner>
                        </Grid>
                    )}
                    <Grid item xs={10}></Grid>
                    <Grid item xs={2}>
                        <Button
                            size={Button.sizes.SMALL}
                            type={Button.types.BUTTON}
                            className="add-user"
                            onClick={handleAddUser}
                        >
                            Add Users
                        </Button>
                    </Grid>
                    <Grid item xs={12} lg={12}>
                    {
                        <UITableReport 
                            columns={columns}
                            data={tableData}
                            isFetching={isFetching}
                            isLoading={isLoading}
                        />
                    }
                    </Grid>
                </Grid>
            </div>

            {openModal && (
                <AddUsersModal 
                    open={openModal} 
                    onClose={handleModalClose}
                    onSave={handleModalSave}
                    setOrganizationRole={setOrganizationRole}
                    emailList={emailList}
                    setEmailList={setEmailList}
                />
            )}

            {openUserRoleModal && (
                <UpdateUsersRole 
                    open={openUserRoleModal}
                    onClose={handleUpdateUserModalClose}
                    onSave={handleUpdateUserModalSave}
                    currentRole={currentUserRole}
                    newUserRoleId={newUserRoleId}
                    setNewUserRoleId={setNewUserRoleId}
                />
            )}

            {openRemoveUserModal && (
                <RemoveUserModal 
                    open={openRemoveUserModal}
                    onConfirm={handleRemoveUserConfirm}
                    onClose={handleRemoveUserClose}
                    email={userEmailToRemove}
                    organizationName={localStorage.getItem('OrganizationName')}
                />
            )}
        </>
    )
}

export default UsersForm